import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { Agent } from '../../stores/chatStore'

interface AgentCardProps {
  agent: Agent
}

export default function AgentCard({ agent }: AgentCardProps) {
  const navigate = useNavigate()
  // Agent status computed from probing data
  const isCallable = agent.availability === 'callable'
  const isReachable = agent.availability === 'reachable'
  const statusColor = isCallable ? 'var(--status-online)' : isReachable ? 'var(--accent-gold)' : 'var(--text-muted)'
  const statusLabel = isCallable ? '✅ Callable' : isReachable ? '🟡 Reachable' : '🧪 Experimental'

  const handleClick = () => {
    navigate(`/agents/${encodeURIComponent(agent.agent_key)}`)
  }

  return (
    <motion.div
      className="p-4 rounded-lg border border-[var(--border-default)] bg-[var(--bg-secondary)] cursor-pointer transition-colors hover:border-[var(--accent-teal)]/50 hover:bg-[var(--bg-secondary)]/80"
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Agent image/avatar */}
          {agent.image_url ? (
            <img
              src={agent.image_url}
              alt=""
              className="w-10 h-10 rounded-lg object-cover bg-[var(--bg-tertiary)]"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center">
              <span className="text-[var(--text-muted)] font-mono text-sm">
                {(agent.name || agent.token_id)?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          )}

          {/* Name and status */}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-mono font-semibold text-sm truncate">
                {agent.name || `Agent #${agent.token_id}`}
              </h3>
              {/* Status indicator */}
              <span className="text-xs font-mono" style={{ color: statusColor }}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Protocol badges + arrow */}
        <div className="flex items-center gap-3">
          <div className="flex flex-wrap gap-1.5 justify-end">
            {getUniqueProtocols(agent.endpoints).map((protocol) => (
              <ProtocolBadge key={protocol} protocol={protocol} />
            ))}
          </div>
          <ArrowIcon className="w-4 h-4 text-[var(--text-muted)]" />
        </div>
      </div>

      {/* Description */}
      {agent.description && (
        <p className="text-xs text-[var(--text-secondary)] font-mono mb-3 line-clamp-2">
          {agent.description}
        </p>
      )}

      {/* Metrics */}
      <div className="flex items-center gap-6">
        {agent.reputation_score !== null && agent.reputation_count !== null && agent.reputation_count > 0 && (
          <MetricBar
            label="Reputation"
            value={agent.reputation_score}
            max={5}
            color="var(--accent-teal)"
          />
        )}
        {(agent.x402_support === 'true' ||
          agent.metadata?.x402Support === true ||
          (agent.metadata?.additional_fields as Record<string, unknown>)?.x402support === true ||
          (agent.metadata?.additional_fields as Record<string, unknown>)?.x402Support === true) && (
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--accent-gold)] text-xs">$</span>
            <span className="text-xs text-[var(--text-muted)] font-mono">x402</span>
          </div>
        )}
        {agent.domain_verified && (
          <div className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-[var(--status-online)]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-[var(--status-online)] font-mono">Verified</span>
          </div>
        )}
      </div>
    </motion.div>
  )
}

function ProtocolBadge({ protocol }: { protocol: string }) {
  const colors: Record<string, string> = {
    mcp: 'var(--badge-mcp)',
    a2a: 'var(--badge-a2a)',
    oasf: 'var(--badge-oasf)',
    web: 'var(--badge-web)',
    wallet: 'var(--badge-wallet)',
    http_unknown: 'var(--text-muted)',
  }

  const color = colors[protocol] || colors.http_unknown

  return (
    <span
      className="px-2 py-0.5 text-xs font-mono font-medium rounded uppercase"
      style={{
        backgroundColor: `${color}20`,
        color: color,
      }}
    >
      {protocol === 'http_unknown' ? 'HTTP' : protocol}
    </span>
  )
}

function MetricBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const percentage = Math.min((value / max) * 100, 100)

  return (
    <div className="flex-1 max-w-32">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--text-muted)] font-mono">{label}</span>
        <span className="text-xs font-mono" style={{ color }}>{value} / {max}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[var(--bg-tertiary)] overflow-hidden">
        <motion.div
          className="h-full rounded-full progress-bar"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
      </div>
    </div>
  )
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
}

function getUniqueProtocols(endpoints: Agent['endpoints']): string[] {
  return [...new Set(endpoints.map(e => (e as any).protocol || e.protocol_type).filter(Boolean))]
}
